<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Hero Section -->
    <section class="home-hero relative min-h-screen flex items-center justify-center overflow-hidden">
      <!-- Enhanced Background -->
      <CosmicBackground />
      <div class="absolute inset-0 page-overlay-layer bg-gradient-to-br from-blue-200/18 via-slate-100/8 to-indigo-200/16 dark:from-dark-bg/55 dark:via-primary-900/35 dark:to-dark-bg/62"></div>
      <div class="absolute inset-0 page-overlay-layer opacity-14 dark:opacity-28 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.2),transparent_36%),radial-gradient(circle_at_80%_30%,rgba(99,102,241,0.2),transparent_40%),radial-gradient(circle_at_50%_80%,rgba(236,72,153,0.15),transparent_38%)]"></div>
      <div class="absolute inset-0 page-overlay-layer home-hero-vignette"></div>
      <div class="absolute inset-0 page-overlay-layer home-hero-motion"></div>

      <!-- Hero Visual -->
      <div class="absolute inset-0 page-overlay-layer hidden lg:block">
        <HeroVisual />
      </div>

      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        <div class="animate-fade-in">
          <div class="inline-block mb-6 px-6 py-2 rounded-full bg-primary-10/80 backdrop-blur-md border border-primary-20/50 shadow-lg">
            <span class="text-primary-adaptive text-sm font-medium">新一代数字营销平台</span>
          </div>

          <h1 class="text-6xl md:text-8xl font-bold mb-8 leading-tight">
            <span class="gradient-text-animated">智能营销</span>
            <br>
            <span class="text-gray-900 dark:text-dark-text">驱动未来增长</span>
          </h1>

          <p class="text-xl md:text-2xl text-gray-700 dark:text-dark-muted mb-12 max-w-4xl mx-auto leading-relaxed">
            基于AI和大数据的全方位数字营销解决方案，助力企业在数字化浪潮中实现精准获客与业务指数级增长
          </p>

          <div class="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button @click="scrollToAboutContact" class="btn-primary text-lg group pulse-glow">
              <span class="relative z-10">立即开始</span>
              <svg class="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </button>
            <!-- <button class="btn-secondary text-lg flex items-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              观看演示
            </button> -->
          </div>
        </div>

        <!-- Enhanced Data Display -->
        <div class="mt-20 mb-12 md:mb-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div v-for="(stat, index) in stats" :key="index"
               class="text-center animate-slide-up h-full flex flex-col opacity-0"
               :style="{ animationDelay: `${index * 0.15}s`, animationFillMode: 'forwards' }">
            <div class="glass-card-enhanced p-6 card-hover h-full flex flex-col justify-center ring-1 ring-indigo-400/25 dark:ring-indigo-200/15">
              <div class="text-4xl md:text-5xl font-bold gradient-text-animated mb-2 whitespace-nowrap">{{ stat.value }}</div>
              <div class="text-gray-700 dark:text-dark-muted text-sm">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心优势 -->
    <section class="py-32 relative">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <h2 class="section-title gradient-text">核心技术优势</h2>
          <p class="text-xl text-gray-700 dark:text-dark-muted mt-6 max-w-3xl mx-auto">
            采用业界领先的AI技术和大数据处理能力，为您的业务提供强大支撑
          </p>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(feature, index) in features" :key="index"
               class="card card-hover group visual-surface">
            <div class="w-16 h-16 mx-auto mb-6 icon-shell group-hover:scale-110 transition-transform duration-500">
              <FeatureIcon :name="feature.iconName || 'ai'" size="xl" variant="minimal" />
            </div>
            <h3 class="text-2xl font-bold mb-4 text-dark-text text-center">{{ feature.title }}</h3>
            <p class="text-gray-700 dark:text-dark-muted mb-6">{{ feature.description }}</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="tag in feature.tags" :key="tag"
                    class="px-3 py-1 bg-primary-10 border border-primary-20 rounded-full text-xs text-primary">
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 产品预览 -->
    <section class="py-32 relative overflow-hidden">
      <div class="absolute inset-0 page-overlay-layer bg-gradient-to-r from-primary-5 via-accent-5 to-primary-5"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <h2 class="section-title gradient-text">产品生态体系</h2>
          <p class="text-xl text-gray-700 dark:text-dark-muted mt-6 max-w-3xl mx-auto">
            六大核心产品构建完整数字营销生态，覆盖从数据管理到投放转化的全链路
          </p>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="product in products" :key="product.id" 
               class="group relative">
            <div class="card h-full card-hover border border-primary-20 dark:border-dark-border visual-surface overflow-hidden">
              <div class="card-corner-accent" aria-hidden="true"></div>
              
              <div class="relative z-10">
                <div class="w-16 h-16 icon-shell icon-shell-strong mb-6 group-hover:scale-110 transition-transform duration-500">
                  <FeatureIcon :name="product.iconName || 'targeting'" size="xl" variant="minimal" />
                </div>
                
                <h3 class="text-2xl font-bold mb-3 text-dark-text">{{ product.title }}</h3>
                <p class="text-primary font-medium mb-4 dark:text-primary-700">{{ product.subtitle }}</p>
                <p class="text-gray-700 dark:text-dark-muted text-sm mb-6">{{ product.description }}</p>
                
                <div class="space-y-2 mb-6">
                  <div v-for="feature in product.features" :key="feature" 
                       class="flex items-center text-sm text-gray-800 dark:text-dark-text">
                    <svg class="w-4 h-4 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ feature }}
                  </div>
                </div>
                
                <NuxtLink :to="`/products/${product.slug}`" 
                          class="inline-flex items-center text-primary hover:text-primary-700 transition-colors duration-300 group/link">
                  了解详情
                  <svg class="w-4 h-4 ml-2 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-16">
          <NuxtLink to="/products" class="btn-secondary inline-flex items-center">
            查看全部产品
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
            </svg>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- CTA 区域 -->
    <section id="contact" class="py-32 relative">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">准备好开始了吗？</h2>
        <p class="text-xl text-gray-700 dark:text-dark-muted mb-12">
          立即体验下一代数字营销平台，开启智能营销新时代
        </p>
        
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="scrollToAboutContact" class="btn-primary text-lg">
            免费试用
          </button>
          <button @click="scrollToAboutContact" class="btn-secondary text-lg">
            联系销售
          </button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
const stats = [
  { value: '500+', label: '企业客户' },
  { value: '98%', label: '客户满意度' },
  { value: '300亿+', label: '日处理请求' },
  { value: '99.9%', label: '系统可用性' }
]

const features = [
  {
    icon: '🤖',
    iconName: 'ai',
    title: 'AI智能算法',
    description: '基于深度学习的智能投放系统，自动优化广告效果',
    tags: ['机器学习', '智能预测', '自动优化']
  },
  {
    icon: '📊',
    iconName: 'data',
    title: '大数据分析',
    description: 'TB级数据处理能力，深度挖掘用户行为和商业价值',
    tags: ['实时分析', '用户画像', '精准定向']
  },
  {
    icon: '⚡',
    iconName: 'speed',
    title: '毫秒级响应',
    description: '分布式架构设计，确保高并发场景下的快速响应',
    tags: ['高性能', '低延迟', '高并发']
  },
  {
    icon: '🔒',
    iconName: 'shield-check',
    title: '数据安全',
    description: '企业级安全防护，符合国际数据保护标准',
    tags: ['加密传输', '权限管理', '安全审计']
  },
  {
    icon: '🌐',
    iconName: 'global',
    title: '全球覆盖',
    description: '覆盖200+国家和地区，支持多语言和本地化运营',
    tags: ['跨境投放', '本地化', '全球CDN']
  },
  {
    icon: '🎯',
    iconName: 'targeting',
    title: '精准投放',
    description: '多维度用户标签，实现真正意义上的精准营销',
    tags: ['用户标签', '行为分析', 'LBS定位']
  }
]

const products = [
  {
    id: 1,
    title: 'MediaGet SSP',
    subtitle: '媒体管理平台',
    description: '智能化的媒体资源管理系统，帮助媒体方最大化收益',
    icon: '📺',
    iconName: 'ssp',
    slug: 'ssp',
    features: ['实时竞价', '收益优化', '多渠道管理']
  },
  {
    id: 2,
    title: 'Adget DSP',
    subtitle: '广告管理平台',
    description: '精准的广告投放系统，实现跨媒体的智能投放',
    icon: '🎯',
    iconName: 'dsp',
    slug: 'dsp',
    features: ['精准定向', '智能投放', '效果追踪']
  },
  {
    id: 3,
    title: 'MagicAd SDK',
    subtitle: '移动广告SDK',
    description: '安全合规的移动广告变现SDK，通过多项权威认证',
    icon: '📱',
    iconName: 'sdk',
    slug: 'sdk',
    features: ['安全认证', '双端支持', '高收益']
  },
  {
    id: 4,
    title: '海外ADX',
    subtitle: '海外广告交易平台',
    description: '连接全球广告资源，拓展国际市场',
    icon: '🌍',
    iconName: 'global',
    slug: 'adx',
    features: ['全球覆盖', '跨境投放', '本地化运营']
  },
  {
    id: 5,
    title: '开户投流',
    subtitle: '一站式开户服务',
    description: '快速开通各类广告账户，启动专业投放服务',
    icon: '⚡',
    iconName: 'speed',
    slug: 'account',
    features: ['快速开户', '专业代运营', '效果保障']
  },
  {
    id: 6,
    title: '私域流量',
    subtitle: '私域运营解决方案',
    description: '构建企业私域流量池，提升客户终身价值',
    icon: '💎',
    iconName: 'private-domain',
    slug: 'private-domain',
    features: ['用户沉淀', '精准营销', '价值挖掘']
  }
]

const scrollToAboutContact = () => {
  window.location.href = '/about#contact'
}

</script>
